import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import apiClient from "@/lib/api-client";
import type { Company } from "@/api/generated/client/models/Company";
import { updateCompanySchema, type UpdateCompanyForm } from "@/schemas/company";
import { buildUpdatePayload } from "@/lib/form-utils";

export function useCompanySettings(companyId?: number) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<UpdateCompanyForm>({
    name: "",
    phone: "",
    address: "",
    domain: "",
  });

  // Fetch company details
  const { 
    data: company, 
    isLoading,
    error 
  } = useQuery({
    queryKey: [`/api/companies/${companyId}`],
    queryFn: async (): Promise<Company> => {
      if (!companyId) throw new Error("No company ID");
      return await apiClient.companies.getCompany({ companyId }) as Company;
    },
    enabled: !!companyId,
  });

  // Update form when company data loads
  useEffect(() => {
    if (company) {
      setFormData({
        name: company.name || "",
        phone: company.phone || "",
        address: company.address || "",
        domain: company.domain || "",
      });
    }
  }, [company]);

  // Update company mutation
  const updateMutation = useMutation({
    mutationFn: async (data: UpdateCompanyForm) => {
      if (!company?.id) throw new Error("Company ID not found");
      
      // Build update payload with only changed string fields
      const updateData = buildUpdatePayload<UpdateCompanyForm>(
        { name: data.name, phone: data.phone, address: data.address, domain: data.domain },
        { name: company.name, phone: company.phone, address: company.address, domain: company.domain },
        {
          name: { required: true }, // Name is required and cannot be empty
          phone: {}, // Phone is optional and can be cleared
          address: {}, // Address is optional and can be cleared
          domain: {}, // Domain is optional and can be cleared
        }
      );
      
      // If no fields to update, throw error
      if (Object.keys(updateData).length === 0) {
        throw new Error("No changes to save");
      }
      
      // Validate the data against the schema
      const validatedData = updateCompanySchema.parse(updateData);
      
      const response = await apiClient.companies.updateCompany({
        companyId: company.id,
        updateCompanyRequest: validatedData,
      });
      
      return response;
    },
    onSuccess: (updatedCompany) => {
      toast({
        title: "Account Updated",
        description: "Your company account has been successfully updated.",
      });
      
      // Update the query cache with the new company data
      if (updatedCompany && typeof updatedCompany === 'object' && 'id' in updatedCompany) {
        const updated = updatedCompany as Company;
        
        // Update the company query cache directly
        queryClient.setQueryData([`/api/companies/${updated.id}`], updated);
      }
      
      // Invalidate queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: [`/api/companies/${company?.id}`] });
      setIsEditing(false);
    },
    onError: (error: Error) => {
      // Handle Zod validation errors with detailed messages
      let errorMessage = error.message || "Failed to update account. Please try again.";
      
      if (error.name === "ZodError") {
        // Extract first validation error message
        errorMessage = "Please check your input: " + error.message;
      }
      
      toast({
        title: "Update Failed",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  const handleCancel = () => {
    setFormData({
      name: company?.name || "",
      phone: company?.phone || "",
      address: company?.address || "",
      domain: company?.domain || "",
    });
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  return {
    company,
    isLoading,
    error,
    isEditing,
    formData,
    setFormData,
    handleSubmit,
    handleCancel,
    handleEdit,
    updateMutation,
  };
}
